import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context';
import styles from './LandingPage.module.css';

// Theme hook
function useTheme() {
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('ohcs-theme');
      if (saved === 'light' || saved === 'dark') return saved;
      return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
    }
    return 'dark';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('ohcs-theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === 'dark' ? 'light' : 'dark');

  return { theme, toggleTheme };
}

// Animated counter hook
function useCounter(end: number, duration: number = 2000, start: number = 0) {
  const [count, setCount] = useState(start);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!isVisible) return;

    let startTime: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * (end - start) + start));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration, start]);

  return { count, setIsVisible };
}

export function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loaded, setLoaded] = useState(false);
  const { theme, toggleTheme } = useTheme();

  // Starting with OHCS as pilot - realistic stats for a new system
  const stats = {
    departments: useCounter(5, 2000),
    capacity: useCounter(500000, 2500),
    security: useCounter(100, 1500),
  };

  useEffect(() => {
    // Redirect if already authenticated
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
      return;
    }

    // Trigger animations after mount
    const timer = setTimeout(() => setLoaded(true), 100);

    // Trigger stat counters
    const statsTimer = setTimeout(() => {
      stats.departments.setIsVisible(true);
      stats.capacity.setIsVisible(true);
      stats.security.setIsVisible(true);
    }, 1500);

    return () => {
      clearTimeout(timer);
      clearTimeout(statsTimer);
    };
  }, [isAuthenticated, navigate]);

  const handleGetStarted = () => {
    navigate('/login');
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(0) + 'K';
    return num.toString();
  };

  return (
    <div className={styles.page}>
      {/* Animated Background */}
      <div className={styles.backgroundContainer}>
        <div className={styles.gradientOrb1} />
        <div className={styles.gradientOrb2} />
        <div className={styles.gradientOrb3} />
        <div className={styles.kentePattern} />
        <div className={styles.noiseOverlay} />
      </div>

      {/* Navigation */}
      <nav className={`${styles.nav} ${loaded ? styles.navLoaded : ''}`}>
        <div className={styles.navContent}>
          <div className={styles.logo}>
            <div className={styles.logoIcon}>
              <svg width="40" height="40" viewBox="0 0 48 48" fill="none">
                {/* Folder base */}
                <path
                  d="M4 14C4 11.7909 5.79086 10 8 10H18L22 14H40C42.2091 14 44 15.7909 44 18V38C44 40.2091 42.2091 42 40 42H8C5.79086 42 4 40.2091 4 38V14Z"
                  className={styles.logoFolderBase}
                />
                {/* Document pages */}
                <rect x="12" y="20" width="24" height="4" rx="1" className={styles.logoDocLine} />
                <rect x="12" y="26" width="18" height="3" rx="1" className={styles.logoDocLine} />
                <rect x="12" y="31" width="20" height="3" rx="1" className={styles.logoDocLine} />
                {/* Ghana star accent */}
                <circle cx="38" cy="8" r="6" className={styles.logoStarCircle} />
                <text x="38" y="11" textAnchor="middle" className={styles.logoStarText}>★</text>
              </svg>
            </div>
            <div className={styles.logoText}>
              <span className={styles.logoTitle}>OHCS</span>
              <span className={styles.logoSubtitle}>Records Management System</span>
            </div>
          </div>
          <div className={styles.navLinks}>
            <a href="#features" className={styles.navLink}>Features</a>
            <a href="#security" className={styles.navLink}>Security</a>
            <a href="#about" className={styles.navLink}>About</a>
            {/* Theme Toggle */}
            <button onClick={toggleTheme} className={styles.themeToggle} aria-label="Toggle theme">
              {theme === 'dark' ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="5" />
                  <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              )}
            </button>
            <button onClick={handleGetStarted} className={styles.navButton}>
              Sign In
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={`${styles.heroContent} ${loaded ? styles.heroLoaded : ''}`}>
          <div className={styles.heroBadge}>
            <span className={styles.badgeDot} />
            <span>Government of Ghana • Office of the Head of Civil Service</span>
          </div>

          <h1 className={styles.heroTitle}>
            <span className={styles.heroTitleLine1}>Digital Records</span>
            <span className={styles.heroTitleLine2}>
              <span className={styles.heroTitleGold}>Management</span> System
            </span>
          </h1>

          <p className={styles.heroDescription}>
            Secure, efficient, and intelligent records management for Ghana's civil service.
            Digitize personnel files, automate workflows, and ensure compliance across all
            Ministries, Departments and Agencies.
          </p>

          <div className={styles.heroActions}>
            <button onClick={handleGetStarted} className={styles.primaryButton}>
              <span>Access Portal</span>
              <div className={styles.buttonShine} />
            </button>
            <a href="#features" className={styles.secondaryButton}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polygon points="10,8 16,12 10,16" fill="currentColor" stroke="none" />
              </svg>
              <span>Learn More</span>
            </a>
          </div>
        </div>

        {/* Hero Visual */}
        <div className={`${styles.heroVisual} ${loaded ? styles.heroVisualLoaded : ''}`}>
          <div className={styles.dashboardPreview}>
            <div className={styles.previewHeader}>
              <div className={styles.previewDots}>
                <span /><span /><span />
              </div>
              <span className={styles.previewTitle}>Personnel File Jackets</span>
            </div>
            <div className={styles.previewContent}>
              <div className={styles.previewSidebar}>
                <div className={styles.previewMenuItem} />
                <div className={styles.previewMenuItem} />
                <div className={styles.previewMenuItemActive} />
                <div className={styles.previewMenuItem} />
              </div>
              <div className={styles.previewMain}>
                <div className={styles.previewCard}>
                  <div className={styles.previewCardHeader} />
                  <div className={styles.previewCardRow} />
                  <div className={styles.previewCardRow} />
                  <div className={styles.previewCardRow} />
                </div>
                <div className={styles.previewCard}>
                  <div className={styles.previewCardHeader} />
                  <div className={styles.previewCardRow} />
                  <div className={styles.previewCardRow} />
                </div>
              </div>
            </div>
          </div>
          <div className={styles.floatingCard1}>
            <div className={styles.floatingCardIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <span>PF/OHCS/2026/0847</span>
            <div className={styles.floatingCardStatus}>Active</div>
          </div>
          <div className={styles.floatingCard2}>
            <div className={styles.floatingCardIconSmall}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4" />
                <circle cx="12" cy="12" r="10" />
              </svg>
            </div>
            <span>Workflow Approved</span>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className={`${styles.stats} ${loaded ? styles.statsLoaded : ''}`}>
        <div className={styles.statsIntro}>
          <span className={styles.statsPilot}>Now Live</span>
          <h3 className={styles.statsHeadline}>Powering OHCS Operations</h3>
          <p className={styles.statsSubtext}>
            Already transforming records management at the Office of the Head of Civil Service,
            with a nationwide rollout to all MDAs on the horizon.
          </p>
        </div>
        <div className={styles.statsGrid}>
          <div className={styles.statItem}>
            <div className={styles.statIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <div className={styles.statValue}>{stats.departments.count}</div>
            <div className={styles.statLabel}>OHCS Departments</div>
            <div className={styles.statDescription}>Currently onboarded</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <div className={styles.statValue}>{formatNumber(stats.capacity.count)}+</div>
            <div className={styles.statLabel}>Records Capacity</div>
            <div className={styles.statDescription}>Personnel files supported</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
            </div>
            <div className={styles.statValue}>{stats.security.count}%</div>
            <div className={styles.statLabel}>Compliance Ready</div>
            <div className={styles.statDescription}>Government standards met</div>
          </div>
        </div>
        <div className={styles.statsRoadmap}>
          <div className={styles.roadmapItem}>
            <div className={styles.roadmapDot} />
            <span>Phase 1: OHCS Deployment</span>
            <span className={styles.roadmapStatus}>Live</span>
          </div>
          <div className={styles.roadmapLine} />
          <div className={styles.roadmapItem}>
            <div className={styles.roadmapDotPending} />
            <span>Phase 2: Ministry Rollout</span>
            <span className={styles.roadmapStatusPending}>Coming Soon</span>
          </div>
          <div className={styles.roadmapLine} />
          <div className={styles.roadmapItem}>
            <div className={styles.roadmapDotPending} />
            <span>Phase 3: All MDAs Nationwide</span>
            <span className={styles.roadmapStatusPending}>Planned</span>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className={styles.features}>
        <div className={styles.featuresHeader}>
          <span className={styles.sectionTag}>Capabilities</span>
          <h2 className={styles.sectionTitle}>
            Enterprise-Grade <span className={styles.titleAccent}>Records Management</span>
          </h2>
          <p className={styles.sectionDescription}>
            Comprehensive tools designed for the unique needs of Ghana's civil service
          </p>
        </div>

        <div className={styles.featuresGrid}>
          {/* Personnel File Jackets - Large Feature */}
          <div className={`${styles.featureCard} ${styles.featureCardLarge}`}>
            <div className={styles.featureGlow} />
            <div className={styles.featureIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                <path d="M12 11v6M9 14h6" />
              </svg>
            </div>
            <h3 className={styles.featureTitle}>Personnel File Jackets (PFiles)</h3>
            <p className={styles.featureDescription}>
              Digital-first file jacket system mirroring physical records management.
              Track custody, location, checkout history, and maintain complete audit trails
              for every civil servant's personnel file across all MDAs.
            </p>
            <div className={styles.featureTags}>
              <span>Custody Tracking</span>
              <span>Checkout/Checkin</span>
              <span>Movement History</span>
              <span>File Requests</span>
            </div>
          </div>

          {/* Document Management */}
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
              </svg>
            </div>
            <h3 className={styles.featureTitle}>Records Management</h3>
            <p className={styles.featureDescription}>
              Secure upload, versioning, and retrieval with 5-level security classifications
              from Unclassified to Top Secret.
            </p>
            <div className={styles.featureStats}>
              <div className={styles.featureStat}>
                <span className={styles.featureStatValue}>5</span>
                <span className={styles.featureStatLabel}>Security Levels</span>
              </div>
            </div>
          </div>

          {/* Workflow Automation */}
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
              </svg>
            </div>
            <h3 className={styles.featureTitle}>Workflow Automation</h3>
            <p className={styles.featureDescription}>
              Multi-step approval workflows with role-based routing and delegation chains
              for leave requests, transfers, and promotions.
            </p>
            <div className={styles.featureStats}>
              <div className={styles.featureStat}>
                <span className={styles.featureStatValue}>∞</span>
                <span className={styles.featureStatLabel}>Steps Supported</span>
              </div>
            </div>
          </div>

          {/* AI-Powered OCR - Large Feature */}
          <div className={`${styles.featureCard} ${styles.featureCardLarge}`}>
            <div className={styles.featureGlow} />
            <div className={styles.featureIconAI}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2a4 4 0 0 1 4 4v2h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2h2V6a4 4 0 0 1 4-4z" />
                <circle cx="9" cy="14" r="1.5" />
                <circle cx="15" cy="14" r="1.5" />
                <path d="M9 18h6" />
              </svg>
              <span className={styles.aiSparkle}>✦</span>
            </div>
            <h3 className={styles.featureTitle}>AI-Powered Records OCR</h3>
            <p className={styles.featureDescription}>
              Intelligent text extraction from scanned records using Tesseract.js.
              Automatically digitize legacy paper records, extract metadata, and make
              historical records fully searchable within seconds.
            </p>
            <div className={styles.featureTags}>
              <span>Tesseract.js Engine</span>
              <span>Multi-Language</span>
              <span>Batch Processing</span>
              <span>Auto-Classification</span>
            </div>
          </div>

          {/* Multi-Tenant Architecture */}
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 21h18M3 7v1a3 3 0 0 0 6 0V7m0 1a3 3 0 0 0 6 0V7m0 1a3 3 0 0 0 6 0V7H3l2-4h14l2 4M5 21V10.85M19 21V10.85M9 21v-4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v4" />
              </svg>
            </div>
            <h3 className={styles.featureTitle}>Multi-Tenant MDA System</h3>
            <p className={styles.featureDescription}>
              Complete data isolation between Ministries, Departments, and Agencies with
              unified platform administration.
            </p>
            <div className={styles.featureStats}>
              <div className={styles.featureStat}>
                <span className={styles.featureStatValue}>100%</span>
                <span className={styles.featureStatLabel}>Data Isolation</span>
              </div>
            </div>
          </div>

          {/* Offline-First PWA */}
          <div className={styles.featureCard}>
            <div className={styles.featureIconOffline}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M8.5 16.5L12 13l3.5 3.5M12 13v9" />
                <path d="M20.88 18.09A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.29" />
              </svg>
              <span className={styles.offlineBadge}>PWA</span>
            </div>
            <h3 className={styles.featureTitle}>Offline-First Architecture</h3>
            <p className={styles.featureDescription}>
              Continue working without internet. All changes sync automatically when
              connection resumes via IndexedDB.
            </p>
            <div className={styles.featureStats}>
              <div className={styles.featureStat}>
                <span className={styles.featureStatValue}>24/7</span>
                <span className={styles.featureStatLabel}>Availability</span>
              </div>
            </div>
          </div>

          {/* Advanced Search */}
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
                <path d="M11 8v6M8 11h6" />
              </svg>
            </div>
            <h3 className={styles.featureTitle}>Advanced Search & Reports</h3>
            <p className={styles.featureDescription}>
              Full-text search across all records, OCR content, and metadata with
              powerful filtering and export capabilities.
            </p>
          </div>

          {/* Bulk Operations */}
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
              </svg>
            </div>
            <h3 className={styles.featureTitle}>Bulk Import & Export</h3>
            <p className={styles.featureDescription}>
              Import thousands of records via Excel/CSV. Export reports in multiple formats
              including PDF, Excel, and JSON.
            </p>
          </div>

          {/* Real-time Notifications */}
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                <circle cx="18" cy="5" r="3" fill="currentColor" />
              </svg>
            </div>
            <h3 className={styles.featureTitle}>Real-time Notifications</h3>
            <p className={styles.featureDescription}>
              Instant alerts for workflow approvals, file requests, system updates,
              and audit events.
            </p>
          </div>

          {/* Audit Trail */}
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6" />
                <path d="M12 18v-6M9 15l3 3 3-3" />
              </svg>
            </div>
            <h3 className={styles.featureTitle}>Complete Audit Trail</h3>
            <p className={styles.featureDescription}>
              Every action logged with timestamps, user IDs, and IP addresses for
              full accountability and compliance.
            </p>
          </div>

          {/* Legal Holds & Compliance - Large Feature */}
          <div className={`${styles.featureCard} ${styles.featureCardLarge} ${styles.featureCardCompliance}`}>
            <div className={styles.featureGlow} />
            <div className={styles.featureIconCompliance}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
            </div>
            <h3 className={styles.featureTitle}>Legal Holds & Compliance</h3>
            <p className={styles.featureDescription}>
              Protect records from destruction during litigation or investigations. Apply holds
              to individual files, entire staff records, or by department. Automatic disposition
              blocking ensures compliance with legal requirements and generates comprehensive audit trails.
            </p>
            <div className={styles.featureTags}>
              <span>Litigation Protection</span>
              <span>Disposition Blocking</span>
              <span>Audit Trails</span>
              <span>Compliance Reports</span>
            </div>
          </div>

          {/* Records Archival & PRAAD */}
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 8v13H3V8M1 3h22v5H1z" />
                <path d="M10 12h4" />
              </svg>
            </div>
            <h3 className={styles.featureTitle}>Records Archival & PRAAD</h3>
            <p className={styles.featureDescription}>
              Seamless integration with the Public Records and Archives Administration Department.
              Manage transfer batches, accession numbers, and disposition schedules.
            </p>
            <div className={styles.featureStats}>
              <div className={styles.featureStat}>
                <span className={styles.featureStatValue}>3</span>
                <span className={styles.featureStatLabel}>Transfer Types</span>
              </div>
            </div>
          </div>

          {/* Retirement Processing - Large Feature */}
          <div className={`${styles.featureCard} ${styles.featureCardLarge} ${styles.featureCardCompliance}`}>
            <div className={styles.featureGlow} />
            <div className={styles.featureIconCompliance}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
                <path d="M16 11l2 2 4-4" />
              </svg>
            </div>
            <h3 className={styles.featureTitle}>Retirement Processing</h3>
            <p className={styles.featureDescription}>
              End-to-end retirement management from approaching retirement alerts to final exit
              clearance. Track document checklists, pension processing status, and generate
              comprehensive retirement packages with all required documentation.
            </p>
            <div className={styles.featureTags}>
              <span>90-Day Alerts</span>
              <span>Document Checklists</span>
              <span>Exit Clearance</span>
              <span>Pension Tracking</span>
            </div>
          </div>

          {/* AI Document Classification */}
          <div className={`${styles.featureCard} ${styles.featureCardAI}`}>
            <div className={styles.featureIconAI}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6" />
                <path d="M9 13h6M9 17h3" />
                <circle cx="17" cy="17" r="3" fill="currentColor" />
              </svg>
              <span className={styles.aiSparkle}>✦</span>
            </div>
            <h3 className={styles.featureTitle}>AI Document Classification</h3>
            <p className={styles.featureDescription}>
              Intelligent auto-categorization using AI models. Detect document types,
              extract metadata, and identify PII with confidence scoring.
            </p>
            <div className={styles.featureStats}>
              <div className={styles.featureStat}>
                <span className={styles.featureStatValue}>95%</span>
                <span className={styles.featureStatLabel}>Accuracy</span>
              </div>
            </div>
          </div>

          {/* AI-First Support Chat */}
          <div className={`${styles.featureCard} ${styles.featureCardAI}`}>
            <div className={styles.featureIconAI}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                <path d="M8 10h.01M12 10h.01M16 10h.01" />
              </svg>
              <span className={styles.aiSparkle}>✦</span>
            </div>
            <h3 className={styles.featureTitle}>AI-First Support Chat</h3>
            <p className={styles.featureDescription}>
              24/7 intelligent assistance powered by AI. Get instant help with system features,
              troubleshooting, and best practices with seamless human escalation.
            </p>
            <div className={styles.featureStats}>
              <div className={styles.featureStat}>
                <span className={styles.featureStatValue}>24/7</span>
                <span className={styles.featureStatLabel}>AI Available</span>
              </div>
            </div>
          </div>

          {/* Security Watermarking */}
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
            </div>
            <h3 className={styles.featureTitle}>Security Watermarking</h3>
            <p className={styles.featureDescription}>
              Automatic watermarking on downloads and prints with user identification,
              timestamps, and security classifications for audit compliance.
            </p>
            <div className={styles.featureStats}>
              <div className={styles.featureStat}>
                <span className={styles.featureStatValue}>100%</span>
                <span className={styles.featureStatLabel}>Traceable</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tech Stack Banner */}
        <div className={styles.techStack}>
          <span className={styles.techStackLabel}>Powered By</span>
          <div className={styles.techStackLogos}>
            <div className={styles.techItem}>
              <span className={styles.techIcon}>⚡</span>
              <span>Cloudflare</span>
            </div>
            <div className={styles.techItem}>
              <span className={styles.techIcon}>⚛️</span>
              <span>React</span>
            </div>
            <div className={styles.techItem}>
              <span className={styles.techIcon}>🔷</span>
              <span>TypeScript</span>
            </div>
            <div className={styles.techItem}>
              <span className={styles.techIcon}>🔥</span>
              <span>Hono</span>
            </div>
            <div className={styles.techItem}>
              <span className={styles.techIcon}>💾</span>
              <span>D1 + R2</span>
            </div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section id="security" className={styles.security}>
        <div className={styles.securityContent}>
          <div className={styles.securityText}>
            <span className={styles.sectionTag}>Security</span>
            <h2 className={styles.sectionTitle}>
              Government-Grade <span className={styles.titleAccent}>Protection</span>
            </h2>
            <p className={styles.sectionDescription}>
              Built with the highest security standards to protect sensitive civil service records
            </p>

            <div className={styles.securityList}>
              <div className={styles.securityItem}>
                <div className={styles.securityCheck}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                </div>
                <div>
                  <h4>End-to-End Encryption</h4>
                  <p>All data encrypted at rest and in transit using AES-256</p>
                </div>
              </div>
              <div className={styles.securityItem}>
                <div className={styles.securityCheck}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                </div>
                <div>
                  <h4>Role-Based Access Control</h4>
                  <p>Granular permissions with security clearance levels</p>
                </div>
              </div>
              <div className={styles.securityItem}>
                <div className={styles.securityCheck}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                </div>
                <div>
                  <h4>Complete Audit Logging</h4>
                  <p>Every action tracked for compliance and accountability</p>
                </div>
              </div>
              <div className={styles.securityItem}>
                <div className={styles.securityCheck}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                </div>
                <div>
                  <h4>Data Sovereignty</h4>
                  <p>All data hosted within approved infrastructure</p>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.securityVisual}>
            <div className={styles.securityShield}>
              <svg viewBox="0 0 100 120" fill="none">
                <path
                  d="M50 5 L95 20 L95 55 C95 80 75 100 50 115 C25 100 5 80 5 55 L5 20 Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="url(#shieldGradient)"
                />
                <path
                  d="M35 55 L45 65 L65 45"
                  stroke="currentColor"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={styles.shieldCheck}
                />
                <defs>
                  <linearGradient id="shieldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="rgba(0, 107, 63, 0.3)" />
                    <stop offset="100%" stopColor="rgba(0, 107, 63, 0.1)" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.cta}>
        <div className={styles.ctaContent}>
          <h2 className={styles.ctaTitle}>Ready to Modernize Your Records Management?</h2>
          <p className={styles.ctaDescription}>
            Be part of Ghana's digital transformation — expanding soon to all MDAs nationwide
          </p>
          <button onClick={handleGetStarted} className={styles.ctaButton}>
            <span>Access Your Institution Portal</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer id="about" className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.footerMain}>
            <div className={styles.footerBrand}>
              <div className={styles.footerLogo}>
                <div className={styles.logoIcon}>
                  <svg width="40" height="40" viewBox="0 0 48 48" fill="none">
                    {/* Folder base */}
                    <path
                      d="M4 14C4 11.7909 5.79086 10 8 10H18L22 14H40C42.2091 14 44 15.7909 44 18V38C44 40.2091 42.2091 42 40 42H8C5.79086 42 4 40.2091 4 38V14Z"
                      className={styles.logoFolderBase}
                    />
                    {/* Document pages */}
                    <rect x="12" y="20" width="24" height="4" rx="1" className={styles.logoDocLine} />
                    <rect x="12" y="26" width="18" height="3" rx="1" className={styles.logoDocLine} />
                    <rect x="12" y="31" width="20" height="3" rx="1" className={styles.logoDocLine} />
                    {/* Ghana star accent */}
                    <circle cx="38" cy="8" r="6" className={styles.logoStarCircle} />
                    <text x="38" y="11" textAnchor="middle" className={styles.logoStarText}>★</text>
                  </svg>
                </div>
                <div className={styles.footerLogoText}>
                  <span className={styles.footerLogoTitle}>OHCS</span>
                  <span className={styles.footerLogoSubtitle}>Records Management System</span>
                </div>
              </div>
              <p className={styles.footerDescription}>
                Empowering Ghana's civil service with modern, secure, and efficient
                records management solutions.
              </p>
            </div>
            <div className={styles.footerLinks}>
              <div className={styles.footerColumn}>
                <h4>Platform</h4>
                <a href="#features">Features</a>
                <a href="#security">Security</a>
                <a href="/login">Institution Login</a>
                <a href="/platform/login">Admin Portal</a>
              </div>
              <div className={styles.footerColumn}>
                <h4>Support</h4>
                <a href="#">Documentation</a>
                <a href="#">Training Resources</a>
                <a href="#">Contact Support</a>
                <a href="#">System Status</a>
              </div>
              <div className={styles.footerColumn}>
                <h4>Legal</h4>
                <a href="#">Privacy Policy</a>
                <a href="#">Terms of Service</a>
                <a href="#">Data Protection</a>
              </div>
            </div>
          </div>
          <div className={styles.footerBottom}>
            <div className={styles.footerCopyright}>
              <span>© {new Date().getFullYear()} Office of the Head of Civil Service, Ghana.</span>
              <span>All rights reserved.</span>
            </div>
            <div className={styles.footerBadges}>
              <span className={styles.footerBadge}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                Secure
              </span>
              <span className={styles.footerBadge}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
                24/7 Available
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
